import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { KeyPair } from '@/crypto/sodium';
import {
  collectVault,
  exportFilename,
  type TransferProgress,
} from '@/features/vault/transfer/exportVault';
import { encodeExport } from '@/features/vault/transfer/format';
import {
  EntryList,
  ErrorBox,
  ProgressLine,
  transferErrorText,
} from '@/features/vault/transfer/shared';
import { downloadTextFile } from '@/lib/download';

/** Fișierul poate fi atacat offline oricât: parola de export nu poate fi scurtă. */
const EXPORT_PASSWORD_MIN_LENGTH = 12;

interface ExportOutcome {
  exported: number;
  failed: Array<{ title: string; message: string }>;
}

interface ExportPanelProps {
  tenantId: string;
  tenantSlug: string;
  keyPair: KeyPair;
  onBusyChange: (busy: boolean) => void;
}

export function ExportPanel({ tenantId, tenantSlug, keyPair, onBusyChange }: ExportPanelProps) {
  const { t } = useTranslation();
  const passwordId = useId();
  const confirmId = useId();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [encrypting, setEncrypting] = useState(false);
  const [progress, setProgress] = useState<TransferProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Doar cifre și titluri — secretele decriptate nu ajung în starea componentei.
  const [result, setResult] = useState<ExportOutcome | null>(null);
  const [includeBreakGlass, setIncludeBreakGlass] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // La închiderea dialogului, un export în curs se oprește.
  useEffect(() => () => abortRef.current?.abort(), []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (password.length < EXPORT_PASSWORD_MIN_LENGTH) {
      setError(t('transfer.export.password_too_short', { min: EXPORT_PASSWORD_MIN_LENGTH }));
      return;
    }
    if (password !== confirm) {
      setError(t('transfer.export.password_mismatch'));
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setBusy(true);
    onBusyChange(true);
    try {
      const collected = await collectVault({
        tenantId,
        keyPair,
        includeBreakGlass,
        onProgress: setProgress,
        signal: controller.signal,
      });
      if (collected.cancelled) {
        setError(t('transfer.export.cancelled'));
        return;
      }
      if (collected.credentials.length === 0) {
        setResult({ exported: 0, failed: collected.failed });
        setError(t('transfer.export.nothing'));
        return;
      }
      setEncrypting(true);
      // Lasă mesajul să apară: derivarea Argon2id blochează firul principal.
      await new Promise((resolve) => setTimeout(resolve, 0));
      const file = await encodeExport(collected.credentials, password);
      downloadTextFile(exportFilename(tenantSlug), file);
      setResult({ exported: collected.credentials.length, failed: collected.failed });
      setPassword('');
      setConfirm('');
    } catch (err) {
      setError(transferErrorText(err, t));
    } finally {
      setBusy(false);
      setEncrypting(false);
      setProgress(null);
      onBusyChange(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-sm text-[var(--text-muted)]">{t('transfer.export.intro')}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={passwordId} className="block text-xs font-medium text-[var(--text)] mb-1">
            {t('transfer.export.password_label')}
          </label>
          <input
            id={passwordId}
            type="password"
            className="input"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            required
          />
        </div>
        <div>
          <label htmlFor={confirmId} className="block text-xs font-medium text-[var(--text)] mb-1">
            {t('transfer.export.password_confirm_label')}
          </label>
          <input
            id={confirmId}
            type="password"
            className="input"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={busy}
            required
          />
        </div>
      </div>
      <p className="text-xs text-[var(--text-muted)]">
        {t('transfer.export.password_hint', { min: EXPORT_PASSWORD_MIN_LENGTH })}
      </p>

      <label className="flex items-start gap-2 text-xs text-[var(--text-muted)]">
        <input
          id="export-include-break-glass"
          type="checkbox"
          className="mt-0.5"
          checked={includeBreakGlass}
          onChange={(e) => setIncludeBreakGlass(e.target.checked)}
          disabled={busy}
        />
        <span>{t('transfer.export.include_break_glass')}</span>
      </label>

      {busy && !encrypting && <ProgressLine progress={progress} />}
      {encrypting && (
        <p className="text-xs text-[var(--text-muted)]" aria-live="polite">
          {t('transfer.export.encrypting')}
        </p>
      )}
      {error && <ErrorBox>{error}</ErrorBox>}
      {result && result.exported > 0 && (
        <p className="text-sm text-green-600 dark:text-green-400" role="status">
          {t('transfer.export.done', { count: result.exported })}
        </p>
      )}
      {result && (
        <EntryList
          heading={t('transfer.export.failed_title', { count: result.failed.length })}
          entries={result.failed.map((f) => ({ title: f.title, detail: f.message }))}
          untitled={t('transfer.untitled')}
        />
      )}

      <div className="flex gap-2 pt-1">
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? t('transfer.export.working') : t('transfer.export.submit')}
        </button>
        {busy && !encrypting && (
          <button type="button" className="btn-ghost" onClick={() => abortRef.current?.abort()}>
            {t('common.cancel')}
          </button>
        )}
      </div>
    </form>
  );
}
